import { useCallback } from 'react';
import { useDisclosure } from '@mantine/hooks';
import {
  useBulkAddSpansToListMutation,
  useCreateListMutation,
  useGetListsQuery,
} from '@/features/customListSlice';
import { useAppSelector } from '@/app/hooks';
import { type FilterState } from '@/features/filterSlice';
import {
  addGroupToFilter,
  generateBase64FilterQuery,
} from '@/features/filterHelpers';
import { useClientTerminology } from '@/views/list/NetworkTerms';

import type { CustomListCreate } from '@/generated/types/navigator/types.gen';
import TagManager from 'react-gtm-module';
import { GoogleEvents } from '@/features/googleEvents';
import { CustomListOptions } from './CustomListOptions';
import ListDetailsForm from './ListDetailsForm';
import { useRunListAction } from '@/views/list/custom-lists/customListSelectorUtils';

interface CustomListBulkSelectorProps {
  onCloseHandler: () => void;
  filteredState?: FilterState;
}

export function CustomListBulkSelector({
  onCloseHandler,
  filteredState,
}: CustomListBulkSelectorProps) {
  const { data: lists } = useGetListsQuery({
    sortBy: 'name',
    sortOrder: 'asc',
  });
  const globalFilterQuery = useAppSelector((state) => state.filterQuery);
  const { activeGroupName, selectedNetworkGroup } = useAppSelector(
    (state) => state.analysisMap
  );
  // Use provided filtered state or fall back to global state
  let filterQuery = filteredState || globalFilterQuery;

  if (activeGroupName) {
    filterQuery = addGroupToFilter(
      activeGroupName,
      selectedNetworkGroup,
      filterQuery
    );
  }

  const intl = useClientTerminology();
  const [createList, { isLoading: isCreatingList }] = useCreateListMutation();
  const [bulkAddToList] = useBulkAddSpansToListMutation();

  const [
    newListModalOpened,
    { open: openNewListModal, close: closeNewListModal },
  ] = useDisclosure(false);

  const runListAction = useRunListAction(onCloseHandler);

  const handleItemClick = useCallback(
    (id: string) => {
      if (id === 'new') {
        TagManager.dataLayer({
          dataLayer: {
            event: GoogleEvents.projects.creation.newProject,
          },
        });
        openNewListModal();
      } else {
        const selectedList = lists?.find((l) => l.id === id);
        if (!selectedList) return;
        runListAction(async () => {
          TagManager.dataLayer({
            dataLayer: {
              event: GoogleEvents.projects.adding.bulk,
              params: {
                projectName: selectedList.name,
                projectDescription: selectedList.description,
                filterQuery,
              },
            },
          });
          return bulkAddToList({
            body: {
              addition_filter: generateBase64FilterQuery(filterQuery),
            },
            listId: id,
          }).unwrap();
        });
      }
    },
    [openNewListModal, lists, runListAction, bulkAddToList, filterQuery]
  );

  const handleNewListSubmit = async (values: {
    name: string;
    description?: string;
  }) => {
    TagManager.dataLayer({
      dataLayer: {
        event: GoogleEvents.projects.creation.newProjectSaved,
        params: {
          projectName: values.name,
          projectDescription: values.description,
          filterQuery,
        },
      },
    });

    const createListProps: CustomListCreate = {
      name: values.name,
      description: values.description ?? '',
      creationFilter: generateBase64FilterQuery(filterQuery),
    };

    await runListAction(async () => await createList(createListProps).unwrap());
    closeNewListModal();
  };

  return (
    <>
      <CustomListOptions onItemClick={handleItemClick} />
      <ListDetailsForm
        opened={newListModalOpened}
        onClose={closeNewListModal}
        onSubmit={handleNewListSubmit}
        isSaving={isCreatingList}
        title={intl.formatMessage({
          id: 'customList.addToNewList',
        })}
        submitButtonText={intl.formatMessage({
          id: 'customList.addToList',
        })}
      />
    </>
  );
}
